import { useState, useEffect } from "react";
import { DashboardLayout } from "../components/dashboard/dashboard-layout";
import { PageHeader } from "../components/dashboard/page-header";
import { 
  Monitor, 
  Moon, 
  Sun, 
  Globe, 
  Bell, 
  Shield, 
  Key, 
  Users, 
  Save,
  Check
} from "lucide-react";

export default function SettingsPage() {
  const [activeTab, setActiveTab] = useState("general");
  const [language, setLanguage] = useState("english");
  const [timezone, setTimezone] = useState("(GMT-08:00) Pacific Time");
  const [dateFormat, setDateFormat] = useState("MM/DD/YYYY");
  const [saveSuccess, setSaveSuccess] = useState(false);
  
  // Theme state with proper initialization
  const [theme, setTheme] = useState(() => {
    if (typeof window !== "undefined") {
      // Try to get theme from cookie first, then localStorage, then default to 'system'
      const cookieTheme = document.cookie
        .split('; ')
        .find(row => row.startsWith('theme='))
        ?.split('=')[1];
      return cookieTheme || localStorage.getItem('theme') || 'system';
    }
    return 'system';
  });

  // Apply theme and save to cookie when changed
  useEffect(() => {
    const applyTheme = (selectedTheme: string) => {
      const html = document.documentElement;
      
      if (selectedTheme === 'system') {
        // Remove data-theme attribute and use system preference
        html.removeAttribute('data-theme');
        const isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        html.classList.toggle('dark', isDark);
      } else {
        // Set specific theme
        html.setAttribute('data-theme', selectedTheme);
        html.classList.toggle('dark', selectedTheme === 'dark');
      }
      
      // Save to cookie (expires in 1 year)
      const expires = new Date();
      expires.setFullYear(expires.getFullYear() + 1);
      document.cookie = `theme=${selectedTheme}; expires=${expires.toUTCString()}; path=/`;
      
      // Also save to localStorage as backup
      localStorage.setItem('theme', selectedTheme);
    };

    applyTheme(theme);
  }, [theme]);

  // Listen for system theme changes when theme is set to 'system'
  useEffect(() => {
    if (theme === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = (e: MediaQueryListEvent) => {
        document.documentElement.classList.toggle('dark', e.matches);
      };
      
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, [theme]);

  const handleSaveSettings = () => {
    setSaveSuccess(true);
    
    // Reset success message after 3 seconds
    setTimeout(() => {
      setSaveSuccess(false);
    }, 3000);
  };
  
  return (
    <DashboardLayout>
      <PageHeader 
        title="Settings" 
        description="Manage your application settings and preferences"
        tabs={[
          { id: "general", label: "General" },
          { id: "appearance", label: "Appearance" },
          { id: "notifications", label: "Notifications" },
          { id: "security", label: "Security & Privacy" },
          { id: "team", label: "Team" },
        ]}
        defaultTab="general"
        onTabChange={(tabId) => setActiveTab(tabId)}
      />
      
      {/* Success Message */}
      {saveSuccess && (
        <div className="mb-6 p-4 rounded-lg bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 flex items-center gap-2 animate-fadeIn">
          <Check className="h-5 w-5" />
          <span>Settings saved successfully!</span>
        </div>
      )}
      
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm overflow-hidden border border-gray-200 dark:border-gray-800 mb-6">
        {activeTab === "general" && (
          <div className="p-5">
            <h3 className="text-lg font-semibold mb-6">General Settings</h3>
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Language
                </label>
                <select 
                  className="w-full px-3 py-2 rounded-lg bg-white dark:bg-gray-900 text-sm border border-gray-200 dark:border-gray-800 focus:outline-none focus:ring-1 focus:ring-gray-300 dark:focus:ring-gray-700"
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                >
                  <option value="english">English</option>
                  <option value="spanish">Spanish</option>
                  <option value="french">French</option>
                  <option value="german">German</option>
                  <option value="japanese">Japanese</option>
                  <option value="chinese">Chinese</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Timezone
                </label>
                <select 
                  className="w-full px-3 py-2 rounded-lg bg-white dark:bg-gray-900 text-sm border border-gray-200 dark:border-gray-800 focus:outline-none focus:ring-1 focus:ring-gray-300 dark:focus:ring-gray-700"
                  value={timezone}
                  onChange={(e) => setTimezone(e.target.value)}
                >
                  <option value="(GMT-12:00) International Date Line West">(GMT-12:00) International Date Line West</option>
                  <option value="(GMT-11:00) Midway Island, Samoa">(GMT-11:00) Midway Island, Samoa</option>
                  <option value="(GMT-10:00) Hawaii">(GMT-10:00) Hawaii</option>
                  <option value="(GMT-09:00) Alaska">(GMT-09:00) Alaska</option>
                  <option value="(GMT-08:00) Pacific Time">(GMT-08:00) Pacific Time</option>
                  <option value="(GMT-07:00) Mountain Time">(GMT-07:00) Mountain Time</option>
                  <option value="(GMT-06:00) Central Time">(GMT-06:00) Central Time</option>
                  <option value="(GMT-05:00) Eastern Time">(GMT-05:00) Eastern Time</option>
                  <option value="(GMT-04:00) Atlantic Time">(GMT-04:00) Atlantic Time</option>
                  <option value="(GMT-03:00) Brasilia, Buenos Aires">(GMT-03:00) Brasilia, Buenos Aires</option>
                  <option value="(GMT-02:00) Mid-Atlantic">(GMT-02:00) Mid-Atlantic</option>
                  <option value="(GMT-01:00) Azores, Cape Verde Islands">(GMT-01:00) Azores, Cape Verde Islands</option>
                  <option value="(GMT+00:00) London, Dublin, Lisbon">(GMT+00:00) London, Dublin, Lisbon</option>
                  <option value="(GMT+01:00) Berlin, Paris, Rome, Madrid">(GMT+01:00) Berlin, Paris, Rome, Madrid</option>
                  <option value="(GMT+02:00) Athens, Istanbul, Cairo">(GMT+02:00) Athens, Istanbul, Cairo</option>
                  <option value="(GMT+03:00) Moscow, Baghdad, Riyadh">(GMT+03:00) Moscow, Baghdad, Riyadh</option>
                  <option value="(GMT+04:00) Dubai, Baku, Tbilisi">(GMT+04:00) Dubai, Baku, Tbilisi</option>
                  <option value="(GMT+05:00) Karachi, Tashkent">(GMT+05:00) Karachi, Tashkent</option>
                  <option value="(GMT+05:30) Mumbai, Kolkata, New Delhi">(GMT+05:30) Mumbai, Kolkata, New Delhi</option>
                  <option value="(GMT+06:00) Dhaka, Almaty">(GMT+06:00) Dhaka, Almaty</option>
                  <option value="(GMT+07:00) Bangkok, Jakarta, Hanoi">(GMT+07:00) Bangkok, Jakarta, Hanoi</option>
                  <option value="(GMT+08:00) Beijing, Singapore, Hong Kong">(GMT+08:00) Beijing, Singapore, Hong Kong</option>
                  <option value="(GMT+09:00) Tokyo, Seoul">(GMT+09:00) Tokyo, Seoul</option>
                  <option value="(GMT+10:00) Sydney, Melbourne, Brisbane">(GMT+10:00) Sydney, Melbourne, Brisbane</option>
                  <option value="(GMT+11:00) Solomon Islands, New Caledonia">(GMT+11:00) Solomon Islands, New Caledonia</option>
                  <option value="(GMT+12:00) Auckland, Wellington, Fiji">(GMT+12:00) Auckland, Wellington, Fiji</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Date Format
                </label>
                <select 
                  className="w-full px-3 py-2 rounded-lg bg-white dark:bg-gray-900 text-sm border border-gray-200 dark:border-gray-800 focus:outline-none focus:ring-1 focus:ring-gray-300 dark:focus:ring-gray-700"
                  value={dateFormat}
                  onChange={(e) => setDateFormat(e.target.value)}
                >
                  <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                  <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                  <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                  <option value="MMM DD, YYYY">MMM DD, YYYY</option>
                  <option value="DD MMM YYYY">DD MMM YYYY</option>
                </select>
              </div>
              
              <div className="pt-4">
                <button 
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-white transition-all duration-200"
                  onClick={handleSaveSettings}
                >
                  <Save className="h-4 w-4" />
                  <span>Save Settings</span>
                </button>
              </div>
            </div>
          </div>
        )}
        
        {activeTab === "appearance" && (
          <div className="p-5">
            <h3 className="text-lg font-semibold mb-6">Appearance Settings</h3>
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
                  Theme
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div 
                    className={`p-4 rounded-lg border ${theme === 'light' ? 'border-gray-900 dark:border-gray-100 ring-2 ring-gray-900 dark:ring-gray-100' : 'border-gray-200 dark:border-gray-800'} cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-all duration-200`}
                    onClick={() => setTheme('light')}
                  >
                    <div className="flex items-center justify-center mb-3">
                      <Sun className="h-8 w-8 text-gray-900 dark:text-gray-100" />
                    </div>
                    <div className="text-center">
                      <h4 className="font-medium">Light</h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Light background with dark text</p>
                    </div>
                  </div>
                  
                  <div 
                    className={`p-4 rounded-lg border ${theme === 'dark' ? 'border-gray-900 dark:border-gray-100 ring-2 ring-gray-900 dark:ring-gray-100' : 'border-gray-200 dark:border-gray-800'} cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-all duration-200`}
                    onClick={() => setTheme('dark')}
                  >
                    <div className="flex items-center justify-center mb-3">
                      <Moon className="h-8 w-8 text-gray-900 dark:text-gray-100" />
                    </div>
                    <div className="text-center">
                      <h4 className="font-medium">Dark</h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Dark background with light text</p>
                    </div>
                  </div>
                  
                  <div 
                    className={`p-4 rounded-lg border ${theme === 'system' ? 'border-gray-900 dark:border-gray-100 ring-2 ring-gray-900 dark:ring-gray-100' : 'border-gray-200 dark:border-gray-800'} cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-all duration-200`}
                    onClick={() => setTheme('system')}
                  >
                    <div className="flex items-center justify-center mb-3">
                      <Monitor className="h-8 w-8 text-gray-900 dark:text-gray-100" />
                    </div>
                    <div className="text-center">
                      <h4 className="font-medium">System</h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Follow system preferences</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="pt-4">
                <button 
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-900 dark:bg-gray-100 text-gray-100 dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-gray-50 transition-all duration-200"
                  onClick={handleSaveSettings}
                >
                  <Save className="h-4 w-4" />
                  <span>Save Settings</span>
                </button>
              </div>
            </div>
          </div>
        )}
        
        {activeTab === "notifications" && (
          <div className="p-5">
            <h3 className="text-lg font-semibold mb-6">Notification Settings</h3>
            
            <div className="space-y-6">
              <div>
                <h4 className="text-md font-medium mb-4">Email Notifications</h4>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">System notifications</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Receive emails for system updates and maintenance</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" className="sr-only peer" defaultChecked />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-gray-900 dark:peer-checked:bg-gray-100"></div>
                    </label>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Account activity</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Receive emails for login attempts and security alerts</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" className="sr-only peer" defaultChecked />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-gray-900 dark:peer-checked:bg-gray-100"></div>
                    </label>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Project updates</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Receive emails when projects are updated</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" className="sr-only peer" defaultChecked />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-gray-900 dark:peer-checked:bg-gray-100"></div>
                    </label>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Marketing emails</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Receive promotional emails and newsletters</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" className="sr-only peer" />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-gray-900 dark:peer-checked:bg-gray-100"></div>
                    </label>
                  </div>
                </div>
              </div>
              
              <div className="pt-6 border-t border-gray-200 dark:border-gray-800">
                <h4 className="text-md font-medium mb-4">In-App Notifications</h4>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Task assignments</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Receive notifications when you're assigned to a task</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" className="sr-only peer" defaultChecked />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-gray-900 dark:peer-checked:bg-gray-100"></div>
                    </label>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Comments and mentions</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Receive notifications when someone mentions you</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" className="sr-only peer" defaultChecked />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-gray-900 dark:peer-checked:bg-gray-100"></div>
                    </label>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Project updates</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Receive notifications for project status changes</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" className="sr-only peer" defaultChecked />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-gray-900 dark:peer-checked:bg-gray-100"></div>
                    </label>
                  </div>
                </div>
              </div>
              
              <div className="pt-4">
                <button 
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-white transition-all duration-200"
                  onClick={handleSaveSettings}
                >
                  <Save className="h-4 w-4" />
                  <span>Save Settings</span>
                </button>
              </div>
            </div>
          </div>
        )}
        
        {activeTab === "security" && (
          <div className="p-5">
            <h3 className="text-lg font-semibold mb-6">Security & Privacy Settings</h3>
            
            <div className="space-y-6">
              <div>
                <h4 className="text-md font-medium mb-4">Security</h4>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Two-factor authentication</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Require a security code in addition to your password</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" className="sr-only peer" />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-gray-900 dark:peer-checked:bg-gray-100"></div>
                    </label>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Login notifications</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Receive notifications for new login attempts</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" className="sr-only peer" defaultChecked />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-gray-900 dark:peer-checked:bg-gray-100"></div>
                    </label>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Require password change</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Require password change every 90 days</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" className="sr-only peer" />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-gray-900 dark:peer-checked:bg-gray-100"></div>
                    </label>
                  </div>
                </div>
              </div>
              
              <div className="pt-6 border-t border-gray-200 dark:border-gray-800">
                <h4 className="text-md font-medium mb-4">Privacy</h4>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Profile visibility</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Control who can see your profile information</p>
                    </div>
                    <select className="px-3 py-2 rounded-lg bg-white dark:bg-gray-900 text-sm border border-gray-200 dark:border-gray-800 focus:outline-none focus:ring-1 focus:ring-gray-300 dark:focus:ring-gray-700">
                      <option value="everyone">Everyone</option>
                      <option value="team">Team members only</option>
                      <option value="private">Private</option>
                    </select>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Activity status</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Show when you're active on the platform</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" className="sr-only peer" defaultChecked />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-gray-900 dark:peer-checked:bg-gray-100"></div>
                    </label>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Data collection</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Allow collection of usage data to improve services</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" className="sr-only peer" defaultChecked />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-gray-900 dark:peer-checked:bg-gray-100"></div>
                    </label>
                  </div>
                </div>
              </div>
              
              <div className="pt-6 border-t border-gray-200 dark:border-gray-800">
                <h4 className="text-md font-medium mb-4">Sessions</h4>
                <button className="px-4 py-2 rounded-lg border border-red-200 dark:border-red-900 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all duration-200">
                  Sign Out All Devices
                </button>
              </div>
              
              <div className="pt-4">
                <button 
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-white transition-all duration-200"
                  onClick={handleSaveSettings}
                >
                  <Save className="h-4 w-4" />
                  <span>Save Settings</span>
                </button>
              </div>
            </div>
          </div>
        )}
        
        {activeTab === "team" && (
          <div className="p-5">
            <h3 className="text-lg font-semibold mb-6">Team Settings</h3>
            
            <div className="space-y-6">
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-md font-medium">Team Members</h4>
                  <button className="px-3 py-1.5 rounded-lg bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-white transition-all duration-200 text-sm">
                    Invite Member
                  </button>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-800">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-sm font-medium">
                        JD
                      </div>
                      <div>
                        <p className="font-medium">John Doe (You)</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">john.doe@example.com</p>
                      </div>
                    </div>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                      Admin
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-800">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-sm font-medium">
                        OM
                      </div>
                      <div>
                        <p className="font-medium">Olivia Martin</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">olivia.martin@example.com</p>
                      </div>
                    </div>
                    <select className="px-2 py-1 rounded-lg bg-white dark:bg-gray-900 text-xs border border-gray-200 dark:border-gray-800 focus:outline-none focus:ring-1 focus:ring-gray-300 dark:focus:ring-gray-700">
                      <option value="admin">Admin</option>
                      <option value="editor" selected>Editor</option>
                      <option value="viewer">Viewer</option>
                    </select>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-800">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-sm font-medium">
                        JL
                      </div>
                      <div>
                        <p className="font-medium">Jackson Lee</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">jackson.lee@example.com</p>
                      </div>
                    </div>
                    <select className="px-2 py-1 rounded-lg bg-white dark:bg-gray-900 text-xs border border-gray-200 dark:border-gray-800 focus:outline-none focus:ring-1 focus:ring-gray-300 dark:focus:ring-gray-700">
                      <option value="admin">Admin</option>
                      <option value="editor">Editor</option>
                      <option value="viewer" selected>Viewer</option>
                    </select>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-800">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-sm font-medium">
                        IN
                      </div>
                      <div>
                        <p className="font-medium">Isabella Nguyen</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">isabella.nguyen@example.com</p>
                      </div>
                    </div>
                    <select className="px-2 py-1 rounded-lg bg-white dark:bg-gray-900 text-xs border border-gray-200 dark:border-gray-800 focus:outline-none focus:ring-1 focus:ring-gray-300 dark:focus:ring-gray-700">
                      <option value="admin">Admin</option>
                      <option value="editor" selected>Editor</option>
                      <option value="viewer">Viewer</option>
                    </select>
                  </div>
                </div>
              </div>
              
              <div className="pt-6 border-t border-gray-200 dark:border-gray-800">
                <h4 className="text-md font-medium mb-4">Roles & Permissions</h4>
                <div className="space-y-4">
                  <div className="p-4 rounded-lg border border-gray-200 dark:border-gray-800">
                    <div className="flex items-center justify-between mb-2">
                      <h5 className="font-medium">Admin</h5>
                      <span className="text-xs text-gray-500 dark:text-gray-400">1 member</span>
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">Full access to all settings and features</p>
                    <button className="text-sm text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white">
                      Edit Permissions
                    </button>
                  </div>
                  
                  <div className="p-4 rounded-lg border border-gray-200 dark:border-gray-800">
                    <div className="flex items-center justify-between mb-2">
                      <h5 className="font-medium">Editor</h5>
                      <span className="text-xs text-gray-500 dark:text-gray-400">2 members</span>
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">Can edit content but cannot modify settings</p>
                    <button className="text-sm text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white">
                      Edit Permissions
                    </button>
                  </div>
                  
                  <div className="p-4 rounded-lg border border-gray-200 dark:border-gray-800">
                    <div className="flex items-center justify-between mb-2">
                      <h5 className="font-medium">Viewer</h5>
                      <span className="text-xs text-gray-500 dark:text-gray-400">1 member</span>
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">Can only view content, no editing capabilities</p>
                    <button className="text-sm text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white">
                      Edit Permissions
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="pt-4">
                <button 
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-white transition-all duration-200"
                  onClick={handleSaveSettings}
                >
                  <Save className="h-4 w-4" />
                  <span>Save Settings</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}